import { useState } from 'react';
import { X, TrendingUp, ShoppingBag, Receipt, Award, BarChart3 } from 'lucide-react';
import { EmptyState, SkeletonStatTile } from '../../components/boothly/index.js';
import { fmtCurrencyCompact } from '../../lib/format.js';

// ── Constants ─────────────────────────────────────────────────────────────────

const TIME_RANGES = [
  { id: 'week',  label: 'Diese Woche' },
  { id: 'month', label: 'Diesen Monat' },
  { id: 'year',  label: 'Dieses Jahr' },
  { id: 'all',   label: 'Gesamt' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const getCutoff = (range) => {
  const now = new Date();
  switch (range) {
    case 'week': {
      const d = new Date(now);
      d.setDate(d.getDate() - 7);
      return d;
    }
    case 'month': {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 1);
      return d;
    }
    case 'year': {
      const d = new Date(now);
      d.setFullYear(d.getFullYear() - 1);
      return d;
    }
    case 'all':
    default:
      return null;
  }
};

const getRangeLabel = (range) => {
  switch (range) {
    case 'week':  return 'diese Woche';
    case 'month': return 'diesen Monat';
    case 'year':  return 'dieses Jahr';
    case 'all':   return 'insgesamt';
    default:      return '';
  }
};

// ── Bar chart data computation ─────────────────────────────────────────────────

const getBars = (activeRange, orders) => {
  if (activeRange === 'week') {
    const days = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      const dayStr = d.toISOString().slice(0, 10);
      const dayOrders = orders.filter((o) => o.datum?.startsWith(dayStr));
      const value = dayOrders.reduce((s, o) => s + (o.preis || 0), 0);
      const label = days[d.getDay() === 0 ? 6 : d.getDay() - 1];
      return { label, value };
    });
  }

  if (activeRange === 'month') {
    // Last 4 weeks
    return Array.from({ length: 4 }, (_, i) => {
      const weekEnd = new Date();
      weekEnd.setDate(weekEnd.getDate() - i * 7);
      const weekStart = new Date(weekEnd);
      weekStart.setDate(weekStart.getDate() - 6);
      const startStr = weekStart.toISOString().slice(0, 10);
      const endStr = weekEnd.toISOString().slice(0, 10);
      const weekOrders = orders.filter(
        (o) => o.datum && o.datum >= startStr && o.datum <= endStr
      );
      const value = weekOrders.reduce((s, o) => s + (o.preis || 0), 0);
      return { label: `KW${4 - i}`, value };
    }).reverse();
  }

  // year + all: last 12 months
  const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  return Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setMonth(d.getMonth() - (11 - i));
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const prefix = `${year}-${month}`;
    const monthOrders = orders.filter((o) => o.datum?.startsWith(prefix));
    const value = monthOrders.reduce((s, o) => s + (o.preis || 0), 0);
    return { label: monthNames[d.getMonth()], value };
  });
};

// ── StatsSheet ────────────────────────────────────────────────────────────────

export function StatsSheet({ open, onOpenChange, store, currentRole, dark, loading }) {
  const [activeRange, setActiveRange] = useState('week');

  // Enrich orders with computed preis from channel finance settings.
  // The DB price column is 0 because the create-form never sends it;
  // getOrderRevenue() derives revenue from the configured channel price.
  const orders = (store.orders ?? []).map((o) => ({
    ...o,
    preis: store.getOrderRevenue ? store.getOrderRevenue(o) : (o.preis || 0),
  }));

  // Filter by time range
  const cutoff = getCutoff(activeRange);
  const rangedOrders = cutoff
    ? orders.filter((o) => o.datum && new Date(o.datum) >= cutoff)
    : orders;

  // Main stats
  const totalRevenue = rangedOrders.reduce((sum, o) => sum + (o.preis || 0), 0);
  const orderCount = rangedOrders.length;
  const avgOrderValue = orderCount > 0 ? totalRevenue / orderCount : 0;

  // Top channel (used as "Bestseller" substitute)
  const channelCounts = rangedOrders.reduce((acc, o) => {
    const key = store.getChannelLabel?.(o.channelId) || o.kanal || 'Unbekannt';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const topChannel =
    Object.entries(channelCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '–';
  const topChannelCount =
    Object.entries(channelCounts).sort((a, b) => b[1] - a[1])[0]?.[1] || 0;

  // Bar chart
  const bars = getBars(activeRange, orders);
  const maxValue = Math.max(...bars.map((b) => b.value), 1);

  // Channel breakdown
  const channelRevenue = rangedOrders.reduce((acc, o) => {
    const ch = store.getChannelLabel?.(o.channelId) || o.kanal || 'Unbekannt';
    acc[ch] = (acc[ch] || 0) + (o.preis || 0);
    return acc;
  }, {});
  const totalForBreakdown = Object.values(channelRevenue).reduce((a, b) => a + b, 0);
  const channelBreakdownEntries = Object.entries(channelRevenue)
    .sort((a, b) => b[1] - a[1]);

  const rangeLabel = getRangeLabel(activeRange);

  return (
    <>
      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[20px]"
            style={{ animation: 'fadeIn 0.2s ease' }}
            onClick={() => onOpenChange(false)}
          />

          {/* Sheet */}
          <div
            data-testid="sheet-stats"
            className="fixed inset-x-0 bottom-0 z-50 flex max-h-[92vh] flex-col rounded-t-3xl"
            style={{
              paddingBottom: 'env(safe-area-inset-bottom)',
              animation: 'slideUpSheet 0.3s cubic-bezier(0.32, 0.72, 0, 1)',
              background: dark ? '#2C2C2E' : '#FFFFFF',
            }}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="bottom-sheet-handle" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-5 pb-4">
              <h2 className="text-xl font-semibold">Statistik</h2>
              <button
                onClick={() => onOpenChange(false)}
                className="ghost-icon-btn"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-5 pb-6 space-y-6">

              {/* Time Range Selector */}
              <div className="scrollable-x -mx-5 px-5">
                <div className="flex gap-2">
                  {TIME_RANGES.map((range) => (
                    <button
                      key={range.id}
                      onClick={() => setActiveRange(range.id)}
                      className={`filter-chip whitespace-nowrap${activeRange === range.id ? ' active' : ''}`}
                    >
                      {range.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Main Stats Grid — Skeleton when loading */}
              {loading ? (
                <div className="grid grid-cols-2 gap-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <SkeletonStatTile key={i} />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {/* Umsatz */}
                  <div className="glass-card glass-shine glass-card-online rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-4 w-4 text-ios-blue" />
                      <span className="text-xs text-muted-foreground">Umsatz</span>
                    </div>
                    <p className="text-3xl font-bold">{fmtCurrencyCompact(totalRevenue)}</p>
                    <p className="text-sm text-muted-foreground">{rangeLabel}</p>
                  </div>

                  {/* Bestellungen */}
                  <div className="glass-card glass-shine rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Bestellungen</span>
                    </div>
                    <p className="text-3xl font-bold">{orderCount}</p>
                    <p className="text-sm text-muted-foreground">{rangeLabel}</p>
                  </div>

                  {/* Durchschnitt */}
                  <div className="glass-card glass-shine rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Receipt className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Durchschnitt</span>
                    </div>
                    <p className="text-3xl font-bold">{fmtCurrencyCompact(avgOrderValue)}</p>
                    <p className="text-sm text-muted-foreground">pro Bestellung</p>
                  </div>

                  {/* Top-Kanal (replaces Bestseller — no item-level data) */}
                  <div className="glass-card glass-shine rounded-2xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Top-Kanal</span>
                    </div>
                    <p className="text-lg font-bold leading-tight truncate">{topChannel}</p>
                    <p className="text-sm text-muted-foreground">
                      {topChannelCount > 0 ? `${topChannelCount} Bestellungen` : 'keine Daten'}
                    </p>
                  </div>
                </div>
              )}

              {/* Revenue Chart — Skeleton when loading */}
              {loading ? (
                <div>
                  <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Umsatz-Verlauf
                  </h3>
                  <div className="bg-[var(--bg-L1)] border border-[var(--border-default)] rounded-2xl p-4 h-48 animate-pulse" />
                </div>
              ) : (
                <div>
                  <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Umsatz-Verlauf
                  </h3>
                  <div className="glass-card glass-shine rounded-2xl p-4">
                    <div className="flex items-end justify-between gap-2 h-32">
                      {bars.map((bar, i) => (
                        <div
                          key={`${bar.label}-${i}`}
                          className="flex-1 flex flex-col items-center gap-2"
                        >
                          <div className="w-full flex-1 flex items-end">
                            <div
                              className={`w-full rounded-t-md transition-[height] duration-300 ${
                                bar.value > 0 ? 'bg-ios-blue' : 'bg-secondary'
                              }`}
                              style={{
                                height: `${maxValue > 0 ? (bar.value / maxValue) * 100 : 0}%`,
                                minHeight: bar.value > 0 ? 4 : 2,
                                transitionDelay: `${i * 50}ms`,
                              }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground">{bar.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Channel Breakdown */}
              {channelBreakdownEntries.length > 0 && (
                <div>
                  <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Nach Kanal
                  </h3>
                  <div className="glass-card glass-shine rounded-2xl p-4 space-y-3">
                    {channelBreakdownEntries.map(([channel, amount]) => {
                      const percent =
                        totalForBreakdown > 0
                          ? Math.round((amount / totalForBreakdown) * 100)
                          : 0;
                      return (
                        <div key={channel} className="flex items-center gap-3">
                          <div className="w-16 text-sm font-medium truncate">{channel}</div>
                          <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                            <div
                              className="h-full rounded-full bg-ios-blue transition-[width] duration-300"
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                          <div className="w-16 text-right text-sm text-muted-foreground">
                            {fmtCurrencyCompact(amount)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {!loading && orderCount === 0 && (
                <EmptyState
                  icon={BarChart3}
                  title="Noch keine Daten"
                  description="Sobald du Bestellungen abwickelst, siehst du hier deine Statistiken."
                />
              )}

            </div>
          </div>
        </>
      )}
    </>
  );
}
